import { Button, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { constants } from "@/global/config/constants";
import { useStorageCache } from "@/global/hooks/useStorageCache";
import { useI18nState } from "@/global/context/I18nProvider";

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const teamConfig = useTeamConfig();
  const teamI18nConfig = teamConfig?.i18nConfig;
  const teamResources = teamI18nConfig?.resources;
  const teamResourcesArr = Object.keys(teamResources || {});
  const lngSelectionCache = useStorageCache(constants.languageSelectionStorageKey); // prettier-ignore
  const i18nState = useI18nState();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleLanguageChange = (language: string) => {
    setAnchorEl(null);
    i18n.changeLanguage(language);
    lngSelectionCache?.set(teamConfig?.teamSchema, language);
    i18nState.set({ locale: language });
  };

  if (!teamI18nConfig || teamResourcesArr?.length <= 1) {
    return null;
  }

  return (
    <>
      <Button
        variant="text"
        size="small"
        id="language-switcher-button"
        aria-controls={isOpen ? "language-switcher" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        startIcon={<GTranslateOutlinedIcon />}
        onClick={(ev) => {
          ev.stopPropagation();
          setAnchorEl(ev.currentTarget);
        }}
        sx={{
          paddingX: "4px",
          borderRadius: "4px",
          fontWeight: 500,
          fontSize: "1rem",
          color: "text.primary",
          textDecoration: "underline",
          textUnderlineOffset: "4px",
          "& .MuiButton-startIcon": {
            marginRight: "6px",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "1.4rem !important",
            color: "grey.700",
          },
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {i18nState?.locale?.toUpperCase()}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {teamResourcesArr?.length > 0 &&
          teamResourcesArr.map((locale, idx) => {
            return (
              <MenuItem key={idx} onClick={() => handleLanguageChange(locale)}>
                <TickIcon selectedLng={locale} />
                <Typography>{constants.localesValuesMap[locale]}</Typography>
              </MenuItem>
            );
          })}
      </Menu>
    </>
  );
}

function TickIcon(props: { selectedLng: string }) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isSelected = i18n.language === props?.selectedLng;

  return (
    <DoneRoundedIcon
      sx={{
        marginRight: 1,
        color: isSelected
          ? theme.palette.primary.main
          : theme.palette.grey[400],
      }}
    />
  );
}
